<template>
  <MainFormLayout layout="two-column">
    <section class="mb-20">
      <h1
        id="uploadFilesLabel"
        class="font-bitter text-3xl font-bold mb-4">
        Manuscript files
      </h1>
      <p class="mb-4">
        Upload the files that support your submission. Additional information may be required at revision stage.
      </p>
      <p class="mb-8">
        You can upload files up to a maximum of <strong>800MB</strong>.
      </p>

      <NotificationComponent
        heading-text="Tip"
        class="mb-8"
        v-if="!!!mainArticle"
        :heading-level="3">
        <template #body>
          <p>
            If you upload your manuscript using a PDF we will attempt to extract the title, abstract and author list.
          </p>
        </template>
      </NotificationComponent>
    </section>

    <section class="mb-8">
      <h2 class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-4">
        Required files
      </h2>
    </section>

    <MainFileUpload
      :error="mainArticleError"
      @clear-error="mainArticleError = undefined"/>

    <CoverLetterUpload
      :error="coverLetterError"
      @clear-error="coverLetterError = undefined" />

    <DasCard
      :error="dasError"
      @clear-error="dasError = undefined"
      @update-error="validateDas"/>

    <section class="mb-20">
      <div class="mb-8">
        <h2 class="font-roboto text-2xl font-bold border-b border-royal-blue-20 pb-4 mb-4">
          Optional files
        </h2>
      </div>
      <SupportingFiles
        ref="esiSupportingFiles"
        name="Supplementary information"
        :fileType="SubmissionFileType.Esi"
        :acceptedFileTypes="[
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.doc',
          '.docx',
          'application/pdf',
          '.pdf',
          'application/x-latex',
          'text/plain',
          '.tex'
        ]"
        acceptedFileList="Please use common, widely known and machine-readable file formats where possible. ZIP files can be used if you have a large number of files."
      />
      <SupportingFiles
        ref="crystalDataSupportingFiles"
        name="Crystal data"
        :fileType="SubmissionFileType.CrystalData"
        :acceptedFileTypes="[
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          '.doc',
          '.docx',
          'application/pdf',
          '.pdf',
          'application/x-latex',
          'text/plain',
          '.tex'
        ]"
        acceptedFileList="PDF or CIF"
      />

      <SupportingFiles
        ref="figuresSupportingFiles"
        name="Figures"
        :fileType="SubmissionFileType.Figure"
        :acceptedFileTypes="[
          'image/tiff',
          'application/postscript',
          'application/pdf',
          '.tiff',
          '.eps',
          '.pdf'
        ]"
        acceptedFileList="TIFF, EPS or PDF"
      />
    </section>

    <PeerReviewOptions
      :error="reviewerAnonymisationError"
      @clear-error="reviewerAnonymisationError = undefined"
    />
    <ThemedCollectionCard
      :error="themedCollectionError"
      @clear-error="themedCollectionError = undefined"
      @update-error="validateThemedCollection"
    />

    <PageValidationErrors
      v-if="showErrors"
      :failed-rules="failedRules"
      variant="error" />
    <ButtonComponent
      :label="nextText"
      variant="primary"
      data-testid="go-to-article-information-page"
      id="uploadFilesNextButton"
      @customClick="next"
    />
  </MainFormLayout>
  <StickyStorageCounter :remainingFileSizeMB ="remainingFileSizeMB "/>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import PageValidationErrors from '../components/common/PageValidationErrors.vue'
import MainFileUpload from '../components/upload/MainArticleUpload.vue'
import { SubmissionFileType, SubmissionFile, SubmissionFileState, VerifyResult, DasOption } from '@/types'
import MainFormLayout from '../layouts/MainFormLayout.vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import router from '../routes'
import validateUploadFilesView from '../services/validation/uploadFiles'
import DasCard from '../components/upload/DasCard.vue'
import ButtonComponent from '../components/ui-components/ButtonComponent.vue'
import ThemedCollectionCard from '../components/upload/ThemedCollectionCard.vue'
import { trackEvent } from '../services/gtm'
import PeerReviewOptions from '../components/upload/PeerReviewOptions.vue'
import { validateField } from '@/services/validation/utils'
import CoverLetterUpload from '@/components/upload/CoverLetterUpload.vue'
import SupportingFiles from '@/components/upload/SupportingFiles.vue'
import NotificationComponent from '@/components/ui-components/NotificationComponent.vue'
import { usePreventNavigation } from '@/store/functions'
import StickyStorageCounter from '@/components/upload/StickyStorageCounter.vue'
import limits from '../constants/limits'

const store = useStore()
const route = useRoute()
const canDisplayErrors = ref(false)
const hasCrystalDataOnLoad = ref(
  !store.getters.submissionFiles.every((file: SubmissionFile) => file.type !== SubmissionFileType.CrystalData),
)
const mainArticleError = ref<string | undefined>(undefined)
const dasError = ref<string | undefined>(undefined)
const themedCollectionError = ref<string | undefined>(undefined)
const reviewerAnonymisationError = ref<string | undefined>(undefined)
const coverLetterError = ref<string | undefined>(undefined)
const { preventNavigation } = usePreventNavigation()

// Refs for optional supporting files components
const esiSupportingFiles = ref<{ clearInlineErrors: () => void } | null>(null)
const crystalDataSupportingFiles = ref<{ clearInlineErrors: () => void } | null>(null)
const figuresSupportingFiles = ref<{ clearInlineErrors: () => void } | null>(null)

onMounted(() => {
  store.commit('_setStep', route.meta.step)
})

const next = async () => {
  // Clear inline errors for optional supporting files
  esiSupportingFiles.value?.clearInlineErrors()
  crystalDataSupportingFiles.value?.clearInlineErrors()
  figuresSupportingFiles.value?.clearInlineErrors()

  await asyncValidateUploadFilesViewWrapper()
  trackEvent({
    event: 'UploadFilesValidationWarnings',
    category: 'Upload Files Validation Warnings',
    action: 'click',
    uploadFilesValidationWarnings: verifyResult.value?.errors ?? [],
  })
  if (canProgress.value) {
    router.push({ name: nextDestination.value })
  }
  else {
    canDisplayErrors.value = true
  }
}

const validateThemedCollection = async () => {
  const radio = await validateField(
    store.state.activeSubmission.themedCollectionResponse,
    'themed-collection-option-required',
  )
  const text = await validateField(
    store.state.activeSubmission.themedCollectionTitle,
    `max-characters-length:${limits.themedCollection.characters.title},Themed collection title must not exceed`,
  )
  themedCollectionError.value = radio?.errors ?? text?.errors ?? undefined
}

const validateDas = async () => {
  const optionResult = await validateField(
    store.state.activeSubmission.files,
    'data-availability-statement-option-required',
  )

  const customTextResult = await validateField(
    store.state.das.dasCustomContent,
    `max-characters-length:${limits.DAS.characters.statement},Data Availability Statement must not exceed`,
  )

  dasError.value = optionResult?.errors?.length
    ? optionResult.errors
    : customTextResult?.errors ?? undefined
}

const mainArticle = computed((): SubmissionFile => {
  return store.getters.submissionFiles.find((file: { type: SubmissionFileType }) => file.type === SubmissionFileType.MainArticle) as SubmissionFile
})

const crystalDataAdded = computed((): boolean => {
  return !hasCrystalDataOnLoad.value && !store.getters.submissionFiles.every((file: SubmissionFile) => file.type !== SubmissionFileType.CrystalData)
})

const dasFileAdded = computed((): boolean => {
  return store.state.activeSubmission.files.some((file: SubmissionFile) => file.type === SubmissionFileType.DAS)
})

const nextText = computed((): string => {
  return store.state.finalReviewVisited && !crystalDataAdded.value && store.state.das.selectedOption ? 'Update and review' : 'Next'
})

const nextDestination = computed((): string => {
  return store.state.finalReviewVisited && !crystalDataAdded.value && store.state.das.selectedOption && dasFileAdded.value ? 'final-review' : 'article-info'
})

const verifyResult = computed<VerifyResult | null>(() => {
  return store.getters.getVerifyResultByName('uploadFilesVerifyResult') as VerifyResult | null
})

const failedRules = computed(() => {
  return verifyResult.value?.failedRules ?? {}
})

const canProgress = computed((): boolean => {
  return verifyResult.value?.valid ?? false
})

const showErrors = computed((): boolean => {
  return (verifyResult.value?.errors?.length ?? 0) > 0 && canDisplayErrors.value
})

const totalFileSizeMB = computed((): number => {
  let copyOfActiveSubmissionFiles = [...store.state.activeSubmission.files]
  if (store.state.das.selectedOption !== DasOption.File
    && store.getters.groupedSubmissionFiles.some((file: { type: SubmissionFileType }) => file.type === SubmissionFileType.DAS)) {
    copyOfActiveSubmissionFiles = copyOfActiveSubmissionFiles.filter(
      (file: { type: SubmissionFileType }) => file.type !== SubmissionFileType.DAS,
    )
  }
  const nonFailedFiles = copyOfActiveSubmissionFiles.filter(file => file.state !== SubmissionFileState.Failure)
  const totalBytes = nonFailedFiles.reduce((sum: number, file: SubmissionFile) => sum + file.size, 0)
  return Math.round((totalBytes / 1000000) * 100) / 100
})

const maxFileSizeMB = 800
const remainingFileSizeMB = computed(() => {
  return Math.round((maxFileSizeMB - totalFileSizeMB.value) * 100) / 100
})

const uploadingFile = computed((): SubmissionFile | undefined => {
  return store.state.activeSubmission.files.find(
    (file: SubmissionFile) => file.state === SubmissionFileState.Uploading,
  )
})

onBeforeRouteLeave(async (to, _from, next) => {
  if (to.name === 'article-type') {
    next()
    return
  }

  if (!mainArticle.value) {
    preventNavigation({
      headingText: 'Manuscript file required',
      bodyContent: 'Please upload a manuscript file before leaving.',
      location: SubmissionFileType.MainArticle,
    })
    next(false)
  }
  else if (uploadingFile.value) {
    preventNavigation({
      headingText: 'Upload in progress',
      bodyContent: 'Please wait for all uploads to finish.',
      location: uploadingFile.value.type,
    })
    next(false)
  }
  else if (!canProgress.value) {
    await asyncValidateUploadFilesViewWrapper()
    canDisplayErrors.value = true
    preventNavigation({
      headingText: 'Information required',
      bodyContent: 'We still need some more information from you before you continue.',
      location: 'Upload Files',
    })
    next(false)
  }
  else {
    next()
  }
})

async function asyncValidateUploadFilesViewWrapper() {
  await validateUploadFilesView(store)

  const [
    mainResult,
    coverLetterResult,
    dasResult,
    themedCollectionRadio,
    themedCollectionText,
    reviewerAnonymisationResult,
  ] = await Promise.all([
    validateField(store.state.activeSubmission.files, 'manuscript-file-required'),
    validateField(store.state.activeSubmission.files, 'cover-letter-required'),
    validateField(store.state.activeSubmission.files, 'data-availability-statement-option-required'),
    validateField(store.state.activeSubmission.themedCollectionResponse, 'themed-collection-option-required'),
    validateField(store.state.activeSubmission.themedCollectionTitle, `max-characters-length:${limits.themedCollection.characters.title},Themed collection title must not exceed`),
    validateField(store.state.activeSubmission.peerReviewAnonymisation, 'anonymised-peer-review-option-required'),
    validateField(store.state.das.dasCustomContent, `max-characters-length:${limits.DAS.characters.statement},Data Availability Statement must not exceed`),
  ])

  mainArticleError.value = mainResult?.errors ?? undefined
  coverLetterError.value = coverLetterResult?.errors ?? undefined
  dasError.value = dasResult?.errors ?? undefined
  themedCollectionError.value
  = themedCollectionRadio?.errors
    ?? themedCollectionText?.errors
    ?? undefined

  reviewerAnonymisationError.value = reviewerAnonymisationResult?.errors ?? undefined
}
</script>
